import { useCallback, useEffect, useState } from 'react'
import {
  adminArchiveCascade,
  adminCreateCascade,
  adminDeleteCascade,
  adminUpdateCascade,
  CASCADE_MAX,
  CASCADE_TEAMS,
  listCascadesOut,
  type CascadeDraft,
  type CascadeOut as Cascade,
} from '../lib/cascadesApi'
import { shopDate } from '../lib/grouping'
import DeleteButton from './DeleteButton'

interface Props {
  token: string
  isAdmin: boolean
  /**
   * In the Meeting's Conclude section: messages written now are tagged with
   * this meeting, and only this meeting's messages are listed.
   */
  meetingId?: string | null
  /**
   * 'group' — an always-open section (Conclude). 'fold' — collapsed under a
   * header with a count (the Headlines tab, where it is history more than news).
   */
  layout: 'group' | 'fold'
  /** Admin only: include archived messages (the Headlines tab's checkbox). */
  showArchived?: boolean
  /** Called after any write, so Meeting can poll straight away. */
  onChanged?: () => void
  /**
   * Bump to re-fetch. In Conclude this is the live recap's count of messages,
   * so a phone following the meeting sees one the presenter just queued.
   */
  refreshKey?: number
}

const EMPTY: CascadeDraft = { body: '', details: '', teams: [] }

/**
 * Cascading messages the shop sends OUT to other teams (0025).
 *
 * Everyone can read them, as with every other list here; only admins write,
 * edit, archive or delete, and the database re-checks that. A message goes to
 * Ninety at the weekday 5:53pm sync and is locked from then on, because the
 * Ninety connector cannot change a message once posted.
 */
export default function CascadeOut({
  token,
  isAdmin,
  meetingId = null,
  layout,
  showArchived = false,
  onChanged,
  refreshKey = 0,
}: Props) {
  const [items, setItems] = useState<Cascade[] | null>(null)
  const [open, setOpen] = useState(false)
  // null = no form; 'new' = writing one; an id = editing that one.
  const [formFor, setFormFor] = useState<string | null>(null)
  const [draft, setDraft] = useState<CascadeDraft>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setItems(await listCascadesOut(token, showArchived))
    } catch (err) {
      setItems([])
      setProblem(err instanceof Error ? err.message : String(err))
    }
  }, [token, showArchived])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const guard = async (fn: () => Promise<void>) => {
    setBusy(true)
    setProblem(null)
    try {
      await fn()
      await load()
      onChanged?.()
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  if (items === null) return null

  const shown = meetingId ? items.filter((c) => c.meeting_id === meetingId) : items
  // Staff with nothing to read get nothing at all on the Headlines tab — an
  // empty fold would just be noise. Conclude always shows the section, so the
  // team sees the question being asked.
  if (layout === 'fold' && !isAdmin && shown.length === 0) return null

  const startNew = () => {
    setDraft(EMPTY)
    setDone(null)
    setProblem(null)
    setFormFor('new')
    setOpen(true)
  }

  const startEdit = (c: Cascade) => {
    setDraft({ body: c.body, details: c.details ?? '', teams: c.targets.map((t) => t.team) })
    setDone(null)
    setProblem(null)
    setFormFor(c.id)
  }

  const toggleTeam = (team: string) =>
    setDraft((d) => ({
      ...d,
      teams: d.teams.includes(team) ? d.teams.filter((t) => t !== team) : [...d.teams, team],
    }))

  const save = () =>
    guard(async () => {
      if (formFor === 'new') {
        await adminCreateCascade(token, draft, meetingId)
        setDone('Queued. It goes to Ninety at the next weekday 5:53pm sync.')
      } else if (formFor) {
        await adminUpdateCascade(token, formFor, draft)
      }
      setFormFor(null)
    })

  // ---------------------------------------------------------------- the form
  const form = () => {
    const left = CASCADE_MAX - draft.body.length
    const valid = draft.body.trim() !== '' && left >= 0 && draft.teams.length > 0
    return (
      <div className="form cascade-form">
        <h3 className="form__title">{formFor === 'new' ? 'Cascade a message' : 'Edit the message'}</h3>
        <p className="form__lead muted">
          One line another team needs to hear. It lands on their Cascading Messages in Ninety
          at the next weekday 5:53pm sync, and can’t be changed from here after that.
        </p>

        <label className="field">
          <span className="field__label">The message</span>
          <textarea
            className="field__input"
            rows={2}
            autoFocus
            value={draft.body}
            placeholder="e.g. Loaner car #3 is out of service until the 14th"
            onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
          />
          <span className={'field__hint' + (left < 0 ? ' field__hint--over' : '')}>
            {left < 0 ? `${Math.abs(left)} characters too long` : `${left} characters left`}
          </span>
        </label>

        <label className="field">
          <span className="field__label">Details (optional)</span>
          <textarea
            className="field__input"
            rows={3}
            value={draft.details}
            onChange={(e) => setDraft((d) => ({ ...d, details: e.target.value }))}
          />
        </label>

        <fieldset className="cascade-form__teams">
          <legend className="field__label">Send to</legend>
          {CASCADE_TEAMS.map((team) => (
            <label key={team} className="check">
              <input
                type="checkbox"
                checked={draft.teams.includes(team)}
                onChange={() => toggleTeam(team)}
              />
              <span>{team}</span>
            </label>
          ))}
        </fieldset>

        <div className="form__actions">
          <button
            type="button"
            className="btn btn--quiet"
            disabled={busy}
            onClick={() => {
              setFormFor(null)
              setProblem(null)
            }}
          >
            Cancel
          </button>
          <button type="button" className="btn" disabled={busy || !valid} onClick={() => void save()}>
            {busy ? 'Saving…' : formFor === 'new' ? 'Queue it' : 'Save'}
          </button>
        </div>
      </div>
    )
  }

  // ---------------------------------------------------------------- one item
  const card = (c: Cascade) => {
    if (formFor === c.id) return <div key={c.id}>{form()}</div>
    return (
      <article key={c.id} className={'card cascade' + (c.archived ? ' card--done' : '')}>
        <p className="headline__body">{c.body}</p>
        {c.details && <p className="cascade__details">{c.details}</p>}

        <div className="cascade__teams">
          {c.targets.map((t) =>
            t.sent_at ? (
              <span key={t.team} className="chip chip--ok">
                {t.team} · in Ninety {shopDate(t.sent_at)}
              </span>
            ) : (
              <span key={t.team} className="chip chip--accent">
                {t.team} · {c.archived ? 'not sent' : 'waiting to send'}
              </span>
            ),
          )}
          {c.archived && <span className="chip chip--low">Archived</span>}
        </div>

        <p className="headline__meta">
          {c.mine ? 'You' : c.created_by_name} · Written {shopDate(c.created_at)}
        </p>

        {isAdmin && (
          <div className="card__actions">
            {!c.locked && !c.archived && (
              <button
                type="button"
                className="btn btn--quiet"
                disabled={busy || formFor !== null}
                onClick={() => startEdit(c)}
              >
                Edit
              </button>
            )}
            <button
              type="button"
              className="btn btn--quiet"
              disabled={busy}
              onClick={() =>
                void guard(async () => {
                  await adminArchiveCascade(token, c.id, !c.archived)
                })
              }
            >
              {c.archived ? 'Restore' : c.locked ? 'Archive' : 'Cancel sending'}
            </button>
            <DeleteButton
              noun="message"
              disabled={busy}
              consequence={
                `“${c.body}” will be removed from the app. ` +
                (c.locked
                  ? 'The copy already in Ninety stays there — delete it in Ninety if it should go too.'
                  : 'It has not been sent, so it will simply never go to Ninety.')
              }
              onDelete={() =>
                guard(async () => {
                  await adminDeleteCascade(token, c.id)
                })
              }
            />
          </div>
        )}
      </article>
    )
  }

  // --------------------------------------------------------------- the whole
  const body = (
    <>
      {done && <p className="banner banner--ok">{done}</p>}
      {problem && <p className="banner">{problem}</p>}
      {layout === 'group' && shown.length === 0 && formFor !== 'new' && (
        <p className="muted meeting-conclude__lead">
          {isAdmin
            ? 'Anything Leadership or Sales needs to hear from this meeting? Cascade it here.'
            : 'Nothing to send to other teams from this meeting yet.'}
        </p>
      )}
      {shown.map(card)}
      {formFor === 'new' && form()}
      {isAdmin && formFor === null && (
        <button type="button" className="btn btn--quiet" disabled={busy} onClick={startNew}>
          + Cascade a message
        </button>
      )}
    </>
  )

  if (layout === 'group') {
    return (
      <div className="group">
        <h2 className="group__label">
          Cascading messages
          {shown.length > 0 && <span className="group__count">{shown.length}</span>}
        </h2>
        {body}
      </div>
    )
  }

  return (
    <div className="fold">
      <button
        type="button"
        className="fold__head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="fold__caret" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        Sent to other teams
        <span className="group__count">{shown.length}</span>
      </button>
      {open && <div className="fold__body">{body}</div>}
    </div>
  )
}
